import { useCallback, useEffect, useReducer, useRef } from 'react';

const listInitialState = {
  data: [],
  total: null,
  index: 0,
  loading: false,
  error: null,
};

const ACTION = {
  REQUEST: 'FETCH_REQUEST',
  SUCCESS: 'FETCH_SUCCESS',
  FAILURE: 'FETCH_FAILURE',
};

const listReducer = (state, action) => {
  switch (action.type) {
    case ACTION.REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case ACTION.SUCCESS:
      return {
        ...state,
        loading: false,
        index: state.index + action.payload.data.length,
        data: state.data.concat(action.payload.data),
        total: action.payload.total,
      };
    case ACTION.FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload.error,
      };
    case ACTION.RESET:
      return listInitialState;
    default:
      return state;
  }
};

/**
 * Custom hook to load a list of items in an infinite scroll fashion. It returns a ref that should
 * be attached to the list container.
 *
 * @param fetchItems function that receives the index of the first item to load and should return
 * a promise with our usual API list response { data: T[], total: number }
 *
 * @returns {{data: [], loading: boolean, error: string | null, listRef: React.MutableRefObject<null>}}
 */
export default function useInfiniteLoader(fetchItems) {
  const listRef = useRef(null);

  const [{ data, loading, index, total, error }, dispatch] = useReducer(
    listReducer,
    listInitialState,
  );

  const loadItems = useCallback(
    async (newIndex, refreshing = false) => {
      if (!refreshing && (loading || data.length === total)) {
        return;
      }

      dispatch({ type: ACTION.REQUEST });
      try {
        const response = await fetchItems(newIndex);
        dispatch({
          type: ACTION.SUCCESS,
          payload: response,
        });
      } catch (err) {
        dispatch({ type: ACTION.FAILURE, payload: { error: err } });
      }
    },
    [fetchItems, data.length, loading, total],
  );

  useEffect(() => {
    loadItems(listInitialState.index);
  }, []);

  const handleScroll = useCallback(() => {
    // we try to load more item everytime we scroll and the bottom of the list is
    // almost visible (2 * clientHeight)
    if (
      listRef.current.scrollHeight - listRef.current.scrollTop <=
      listRef.current.clientHeight * 2
    ) {
      loadItems(index);
    }
  }, [loadItems, loading, index]);

  useEffect(() => {
    listRef.current.addEventListener('scroll', handleScroll);
    return () => listRef.current.removeEventListener('scroll', handleScroll);
  }, [listRef.current, handleScroll]);

  const forceReload = useCallback(() => {
    dispatch({ type: ACTION.RESET });
    loadItems(listInitialState.index, true);
  }, [loadItems]);

  return {
    data,
    loading,
    error,
    listRef,
    forceReload,
  };
}
